import {
  GrpcResponseError,
  useLegacyGrpcRequestCallback,
} from '@aboutyou/data-fetcher';
import {CheckoutV1_GetAccessTokenLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {useCOTracking} from '@aboutyou/tracking';
import {useCallback} from 'react';

export const useGetAccessTokenCallback = () => {
  const getAccessToken = useLegacyGrpcRequestCallback(
    CheckoutV1_GetAccessTokenLazy,
    {retry: false},
  );
  const {track} = useCOTracking();

  return useCallback(
    async ({
      authToken,
      authProvider,
      onAccessTokenRefreshError,
    }: {
      authToken?: string | null;
      authProvider: string | null;
      onAccessTokenRefreshError: VoidFunction;
    }) => {
      if (!authToken) {
        track({
          type: 'AccessTokenReloadErrorEvent',
          errorDetail: 'No authToken',
          code: -1, // no error code available
          authProvider,
        });
        onAccessTokenRefreshError();
        return null;
      }

      return new Promise<{
        accessToken: string;
      } | null>(resolve => {
        getAccessToken
          .mutateAsync({
            session: {
              authToken,
            },
          })
          .then(response => {
            let tokenId;

            /**
             * parse `jti` from access token for tracking purposes
             * `jti` is the JWT ID, which is a unique identifier for the token
             * this is useful for CO investigations
             */
            try {
              const detailsString = response.accessToken.split('.')[1];
              if (detailsString) {
                tokenId = JSON.parse(atob(detailsString)).jti;
              }
            } catch (e) {
              // eslint-disable-next-line no-console -- DO NOTHING is token parsing fails
              console.log(`>> Error parsing access tokenId: ${e}`);
            }

            track({
              type: 'AccessTokenReloadSuccessEvent',
              tokenId,
              authProvider,
            });

            resolve({
              accessToken: response.accessToken,
            });
          })
          .catch(error => {
            track({
              type: 'AccessTokenReloadErrorEvent',
              errorDetail: (error as GrpcResponseError).message,
              code: (error as GrpcResponseError).code,
              authProvider,
            });

            onAccessTokenRefreshError();
            resolve(null);
          });
      });
    },
    [],
  );
};
